'use client'

import { useState } from 'react'
import { ThumbsUp, ThumbsDown, Check } from 'lucide-react'

// "Was this page helpful?" widget. A small client island shown at the
// foot of every article. Posts {slug, helpful, comment?} to
// /api/help/feedback. After a vote it shows an optional one-line
// comment box, then a thank-you state. Failures are swallowed so the
// reader still gets the thank-you — feedback is best-effort, never
// blocking. The article body above stays server-rendered.
export function HelpFeedback({ slug }: { slug: string }) {
  const [helpful, setHelpful] = useState<boolean | null>(null)
  const [comment, setComment] = useState('')
  const [done, setDone] = useState(false)
  const [sending, setSending] = useState(false)

  async function send(value: boolean, withComment?: string) {
    try {
      await fetch('/api/help/feedback', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ slug, helpful: value, comment: withComment || undefined }),
      })
    } catch {
      // best-effort; ignore network errors
    }
  }

  function vote(value: boolean) {
    setHelpful(value)
    // Fire the vote immediately; a follow-up comment sends a second,
    // enriched event.
    void send(value)
  }

  async function submitComment() {
    if (helpful === null) return
    setSending(true)
    await send(helpful, comment.trim())
    setSending(false)
    setDone(true)
  }

  if (done) {
    return (
      <div className="mt-12 flex items-center gap-2 rounded-xl border border-border/60 bg-secondary/30 px-5 py-4 text-sm text-muted-foreground">
        <Check className="h-4 w-4 text-primary" aria-hidden="true" />
        Thanks for the feedback. It helps us make these guides better.
      </div>
    )
  }

  return (
    <div className="mt-12 rounded-xl border border-border/60 bg-secondary/20 px-5 py-4">
      {helpful === null ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-foreground">Was this page helpful?</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => vote(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-3.5 py-1.5 text-sm text-foreground transition hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <ThumbsUp className="h-4 w-4" aria-hidden="true" /> Yes
            </button>
            <button
              type="button"
              onClick={() => vote(false)}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card px-3.5 py-1.5 text-sm text-foreground transition hover:border-primary/40 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              <ThumbsDown className="h-4 w-4" aria-hidden="true" /> No
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void submitComment()
          }}
          className="space-y-3"
        >
          <label htmlFor="help-feedback-comment" className="block text-sm font-medium text-foreground">
            {helpful ? 'Glad it helped. Anything we could add?' : 'Sorry about that. What were you looking for?'}
          </label>
          <textarea
            id="help-feedback-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={1000}
            placeholder="Optional — tell us more"
            className="w-full resize-y rounded-lg border border-border/70 bg-card px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-primary/50"
          />
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-1.5 text-sm font-medium text-background transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:opacity-60"
            >
              {sending ? 'Sending…' : 'Send feedback'}
            </button>
            <button
              type="button"
              onClick={() => setDone(true)}
              className="text-sm text-muted-foreground underline-offset-4 hover:underline"
            >
              Skip
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
